import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  AuthWellKnownEndpoints,
  OidcSecurityService,
  OpenIdConfiguration,
  TokenHelperService
} from 'angular-auth-oidc-client';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { GlobalConfig } from '../../interfaces/global-config.model';
import { KeycloakSetting } from '../../keycloak/keycloak-setting.model';
import { KeycloakService } from '../../keycloak/keycloak.service';
import { Context as LocalStorageContext } from '../context.local-storage';
import { GLOBAL_CONFIG } from '../global-config.injection-token';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private jwtHelper: JwtHelperService;

  private basePath = '';
  private redirectUrlOverride = '';

  private authCodeFlowConfig: OpenIdConfiguration;
  private wellKnownEndpoints: AuthWellKnownEndpoints;

  public keycloakSetting: KeycloakSetting;

  public isLoggedIn$ = new BehaviorSubject<boolean>(false);
  public isAuthModuleSetup$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private oidcSecurityService: OidcSecurityService,
    private tokenHelperService: TokenHelperService,
    private router: Router,
    private keycloakService: KeycloakService,
    @Optional() @Inject(GLOBAL_CONFIG) globalConfig: GlobalConfig
  ) {
    this.jwtHelper = new JwtHelperService();

    if (globalConfig) {
      this.basePath = globalConfig.basePath || this.basePath;
      this.redirectUrlOverride = globalConfig.redirectUrlOverride || this.redirectUrlOverride;
    }

    this.keycloakSetting = keycloakService.getKeycloakSetting();
    this.init();
  }

  private async init() {
    if (this.keycloakSetting && this.keycloakSetting.enabled) {
      this.configureOAuth();
    } else {
      if (!(await this.isLoggedIn().toPromise())) {
        this.router.navigate(['loginpage']);
      }
    }
  }

  public configureOAuth() {
    this.authCodeFlowConfig = {
      stsServer: this.keycloakSetting.authServerUrl,
      client_id: this.keycloakSetting.resource,
      // response_type: 'id_token token',
      response_type: 'code',
      redirect_url: `${this.redirectUrlOverride || window.location.origin}/#/clients`,
      post_logout_redirect_uri: `${this.redirectUrlOverride || window.location.origin}/#/clients`,
      scope: 'openid roles profile email',
      silent_renew: true,
    ignore_nonce_after_refresh: true,
      useRefreshToken: true,
   renewTimeBeforeTokenExpiresInSeconds: 40,
      silent_renew_offset_in_seconds: 0,
      log_console_debug_active: false,
      isauthorizedrace_timeout_in_seconds: 3
    } as OpenIdConfiguration;

    this.wellKnownEndpoints = {
      issuer: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}`,
      jwks_uri: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}/protocol/openid-connect/certs`,
      authorization_endpoint: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}/protocol/openid-connect/auth`,
      token_endpoint: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}/protocol/openid-connect/token`,
      userinfo_endpoint: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}/protocol/openid-connect/userinfo`,
      end_session_endpoint: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}/protocol/openid-connect/logout`,
      check_session_iframe: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}/protocol/openid-connect/login-status-iframe.html`,
      revocation_endpoint: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}/protocol/openid-connect/revoke`,
      introspection_endpoint: `${this.authCodeFlowConfig.stsServer}/realms/${this.keycloakSetting.realm}/protocol/openid-connect/token/introspect`
    };

    this.oidcSecurityService.setupModule(this.authCodeFlowConfig, this.wellKnownEndpoints);

    this.oidcSecurityService
      .getIsAuthorized()
      .pipe(take(1))
      .subscribe(data => {
        this.isLoggedIn$.next(data);
        if (!data) {
          this.oidcSecurityService.authorize();
        }
      });

    if (this.oidcSecurityService.moduleSetup) {
      this.isAuthModuleSetup$.next(true);
      this.doCallbackLogicIfRequired();
    } else {
      this.oidcSecurityService.onModuleSetup.subscribe(() => {
        this.doCallbackLogicIfRequired();
        this.isAuthModuleSetup$.next(true);
      });
    }
  }

  private doCallbackLogicIfRequired() {
    // this.oidcSecurityService.authorizedImplicitFlowCallback(window.location.toString());
    this.oidcSecurityService.authorizedCallbackWithCode(window.location.toString());
  }

  public getClients(): Observable<string[]> {
    const rawToken = this.oidcSecurityService.getToken();
    const tokenPayload = this.tokenHelperService.getPayloadFromToken(rawToken, false) as Record<string, any>;

    const clientsPayoad = tokenPayload.CLIENTS as string;
    return of(clientsPayoad ? clientsPayoad.split(',') : []);
  }

  public getAccessToken(): string {
    if (this.keycloakSetting && this.keycloakSetting.enabled) {
      return this.oidcSecurityService.getToken();
    } else {
      return localStorage.getItem(LocalStorageContext.KEY_TOKEN);
    }
  }

  public logout(): void {
    if (this.keycloakSetting && this.keycloakSetting.enabled) {
      const currentIdToken = this.oidcSecurityService.getIdToken();
      const idToken = currentIdToken && String(currentIdToken) !== 'null' ? currentIdToken : undefined;

      if (idToken) {
        const logoutUrl = `${
          this.wellKnownEndpoints.end_session_endpoint
        }?id_token_hint=${this.oidcSecurityService.getIdToken()}&post_logout_redirect_uri=${
          this.authCodeFlowConfig.post_logout_redirect_uri
        }`;
        this.oidcSecurityService.logoff(() => {
          window.location.href = logoutUrl;
        });
      } else {
        throw new Error('No id_token found');
      }
    } else {
      this.logoutWithoutKeycloak();
    }
  }

  public isLoggedIn(): Observable<boolean> {
    if (this.keycloakSetting && this.keycloakSetting.enabled) {
      return this.isLoggedIn$;
    } else {
      return this.isLoggedInWithoutKeycloak();
    }
  }

  public isAuthModuleSetup(): Observable<boolean> {
    if (this.keycloakSetting && this.keycloakSetting.enabled) {
      return this.isAuthModuleSetup$;
    } else {
      return of(true);
    }
  }

  public isKeycloakEnabled(): boolean {
    return this.keycloakSetting && this.keycloakSetting.enabled;
  }

  public isLoggedInWithoutKeycloak(): Observable<boolean> {
    const token = localStorage.getItem(LocalStorageContext.KEY_TOKEN);
    const isTokenExpired = token != null && !this.jwtHelper.isTokenExpired(token);
    if (isTokenExpired === false) {
      localStorage.removeItem(LocalStorageContext.KEY_TOKEN);
    }
    return of(isTokenExpired);
  }

  private logoutWithoutKeycloak(): void {
    // clear token remove user from local storage to log user out
    localStorage.clear();
    this.router.navigate(['loginpage']);
  }

  public getCurrentUserName(): Observable<string> {
    if (this.keycloakSetting && this.keycloakSetting.enabled) {
      return this.oidcSecurityService.getUserData().pipe(
        map((data: any) => {
          return data.preferred_username;
        })
      );
    } else {
      return of(localStorage.getItem(LocalStorageContext.KEY_CURRENT_USER_NAME));
    }
  }

  public login(username: string, password: string): Observable<any> {
    return this.http
      .post(`${this.basePath}api/authentication`, {
        username,
        password
      })
      .pipe(
        map((response: any) => {
          const token = response && response.token;
          if (token) {
            // set token property
            // store username and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem(
              'currentUser',
              JSON.stringify({
                username,
                token
              })
            );
            localStorage.setItem(LocalStorageContext.KEY_TOKEN, token);
            localStorage.setItem(LocalStorageContext.KEY_CURRENT_USER_NAME, username);
            const userPass = btoa(username + ':' + password);
            localStorage.setItem('currentUser', JSON.stringify({ username, token }));
            localStorage.setItem('userPass', userPass);

            // return true to indicate successful login
            return true;
          } else {
            // return false to indicate failed login
            return false;
          }
        }),
        catchError(this.handleError)
      );
  }

  private handleError(error) {
    // In a real world app, we might use a remote logging infrastructure
    var errMsg;
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        var err = error.error.errorCode + ' ' + error.error.errorMessage;
        errMsg = error.status + ' - ' + (error.statusText || '') + ' ' + err;
      } else if (error.status == 401 || error.status == 403) {
        errMsg = 'Benutzername oder Passwort ist falsch';
      } else if (error.status == 500) {
        errMsg = 'Interner Serverfehler';
      } else {
        errMsg = error.statusText;
      }
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    return throwError(errMsg);
  }
}
